import { useState } from "react";
import { Link } from "react-router-dom";

interface ReferralLink {
  id: string;
  productId: string;
  productTitle: string;
  productImage: string;
  seller: string;
  sellerId: string;
  commission: number;
  price: number;
  referralCode: string;
  referralUrl: string;
  clicks: number;
  conversions: number;
  earnings: number;
  pendingEarnings: number;
  createdAt: string;
  lastClick: string;
  trend: string;
  weeklyClicks: number[];
}

interface Props {
  link: ReferralLink;
}

export default function ReferralLinkCard({ link }: Props) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(link.referralUrl).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const conversionRate = link.clicks > 0 ? ((link.conversions / link.clicks) * 100).toFixed(1) : "0.0";
  const maxClicks = Math.max(...link.weeklyClicks);

  return (
    <div className="bg-white rounded-2xl border border-gray-100 p-5 hover:border-orange-200 transition-all">
      <div className="flex gap-4 mb-4">
        {/* Product Image */}
        <div className="w-16 h-16 rounded-xl overflow-hidden flex-shrink-0 bg-gray-50">
          <img src={link.productImage} alt={link.productTitle} className="w-full h-full object-cover object-top" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div>
              <h3 className="text-sm font-bold text-gray-900 line-clamp-1">{link.productTitle}</h3>
              <Link to={`/factory/${link.sellerId}`} className="text-xs text-orange-500 hover:underline cursor-pointer">{link.seller}</Link>
            </div>
            <div className={`flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-full flex-shrink-0 ${link.trend === "up" ? "bg-green-50 text-green-600" : "bg-red-50 text-red-500"}`}>
              <i className={link.trend === "up" ? "ri-arrow-up-line" : "ri-arrow-down-line"} />
              {link.trend === "up" ? "Trending" : "Slowing"}
            </div>
          </div>
          <div className="flex items-center gap-3 mt-2">
            <span className="text-xs font-semibold text-orange-600 bg-orange-50 px-2 py-0.5 rounded-full">{link.commission}% commission</span>
            <span className="text-xs text-gray-400">${(link.price * link.commission / 100).toFixed(2)} per sale</span>
          </div>
        </div>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-4 gap-3 mb-4">
        {[
          { label: "Clicks", value: link.clicks.toLocaleString(), icon: "ri-cursor-line" },
          { label: "Sales", value: link.conversions.toString(), icon: "ri-shopping-bag-line" },
          { label: "Conv. Rate", value: `${conversionRate}%`, icon: "ri-percent-line" },
          { label: "Earned", value: `$${link.earnings.toFixed(2)}`, icon: "ri-money-dollar-circle-line" },
        ].map((s) => (
          <div key={s.label} className="text-center">
            <p className="text-sm font-bold text-gray-900">{s.value}</p>
            <p className="text-xs text-gray-400">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Mini Bar Chart */}
      <div className="mb-4">
        <p className="text-xs text-gray-400 mb-2">Last 7 days clicks</p>
        <div className="flex items-end gap-1 h-10">
          {link.weeklyClicks.map((val, i) => (
            <div
              key={i}
              className="flex-1 rounded-sm bg-orange-200 transition-all"
              style={{ height: `${(val / maxClicks) * 100}%`, minHeight: "4px" }}
              title={`${val} clicks`}
            />
          ))}
        </div>
        <div className="flex justify-between text-xs text-gray-300 mt-1">
          <span>Mon</span><span>Tue</span><span>Wed</span><span>Thu</span><span>Fri</span><span>Sat</span><span>Sun</span>
        </div>
      </div>

      {/* Referral URL */}
      <div className="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-2 mb-4">
        <i className="ri-link-m text-gray-400 text-sm flex-shrink-0" />
        <span className="text-xs text-gray-500 flex-1 truncate">{link.referralUrl}</span>
        <button
          onClick={handleCopy}
          className={`flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-md transition-colors cursor-pointer whitespace-nowrap ${copied ? "bg-green-100 text-green-600" : "bg-orange-100 text-orange-600 hover:bg-orange-200"}`}
        >
          <i className={copied ? "ri-check-line" : "ri-file-copy-line"} />
          {copied ? "Copied!" : "Copy"}
        </button>
      </div>

      {/* Pending Payout */}
      {link.pendingEarnings > 0 && (
        <div className="flex items-center justify-between bg-amber-50 border border-amber-100 rounded-lg px-3 py-2 mb-4">
          <div className="flex items-center gap-2">
            <i className="ri-time-line text-amber-500 text-sm" />
            <span className="text-xs text-amber-700 font-medium">Pending payout</span>
          </div>
          <span className="text-xs font-bold text-amber-700">${link.pendingEarnings.toFixed(2)}</span>
        </div>
      )}

      {/* Actions */}
      <div className="flex gap-2">
        <button className="flex-1 py-2 border border-gray-200 text-gray-600 text-xs font-semibold rounded-lg hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-1">
          <i className="ri-share-line" /> Share
        </button>
        <button className="flex-1 py-2 border border-gray-200 text-gray-600 text-xs font-semibold rounded-lg hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-1">
          <i className="ri-bar-chart-line" /> Analytics
        </button>
        <Link to={`/factory/${link.sellerId}`} className="flex-1 py-2 bg-orange-500 text-white text-xs font-semibold rounded-lg hover:bg-orange-600 transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-1">
          <i className="ri-store-line" /> Factory
        </Link>
      </div>
    </div>
  );
}
